import React from 'react';
import { Formik, Form } from 'formik';
import { Box, Typography, useMediaQuery } from '@mui/material';
import parse from 'html-react-parser';
import Image from 'next/image';
import {
  FormPublicDesignFragment,
  FormPublicFieldFragment,
} from '../../../graphql/fragment/form.public.fragment';
import { getComponentByType } from '@/components/types';
import { BottomNavigation } from '@/components/bottomNavigation';
import { FieldInputWrapper, StyledBottomNavigation } from './GEOImpact.css';
//import orb from './assets/orb.svg';
import orb from './assets/crop-blurred-circle-4146-2113px.png';
import { BUTTON_TEXT, GUIDANCE_TEXT, QUESTION } from './utils';

interface Props {
  focus: boolean;
  field: FormPublicFieldFragment;
  design: FormPublicDesignFragment;
  save: (data: any) => void;
  next: (values?: any) => void;
  prev: () => void;
  slideIndex: number;
  lastSlideIndex: number;
  // this has to be passed into the component from swiper since we only reset the question's form
  // if the user wants to make another submission in the end page
  //    if we reset form here, it will lose the user's input upon them moving onto the next question
  // formik will automatically reset the form upon seeing it's intialValues change
  // in this case, a flag that tells the form to reset
  submissionCount?: number;
  totalQuestions: number;
}

/**
 * @param input email input
 * @returns if the input is an email
 */
export const isEmail = (input: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
};

const isLink = (input: string): boolean => {
  // Simple, safe URL regex: checks for protocol and at least one "."
  return /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}/i.test(input);
};

export const Question: React.FC<Props> = ({
  field,
  design,
  save,
  next,
  prev,
  slideIndex,
  submissionCount,
  lastSlideIndex,
  totalQuestions,
}) => {
  const InputComponent = getComponentByType(field.type);

  const validateFieldValues = (formValues: any) => {
    const validationErrors: any = {};
    if (field.required) {
      if (field.type === 'email') {
        const value = formValues[field.id];
        if (!value || typeof value !== 'string' || !isEmail(value.trim())) {
          validationErrors[field.id] = 'Please enter a valid email address';
        }
      } else if (
        field.type === 'number' &&
        typeof formValues[field.id] !== 'number'
      ) {
        validationErrors[field.id] = 'Please enter a number';
      } else if (field.type === 'textarea' || field.type === 'textfield') {
        if (!formValues[field.id]) {
          validationErrors[field.id] = 'Please enter a value';
        } else if (formValues[field.id].trim() === '') {
          validationErrors[field.id] = 'Please enter a non-empty value';
        }
      } else if (field.type === 'link' && !isLink(formValues[field.id])) {
        validationErrors[field.id] = 'Please enter a valid link';
      } else if (!formValues[field.id]) {
        validationErrors[field.id] = 'Please enter a value';
      }
    }
    return validationErrors;
  };

  const handleFormSubmit = (formValues: any) => {
    save(formValues);
  };

  const getPatternForField = (field: FormPublicFieldFragment) => {
    const ids = ['R1Wmwj', '5V9ky7'];
    if (ids.includes(field.id)) {
      return '^\\d+$';
    }
    return undefined;
  };

  const createCustomHandleChange = (handleChange: (e: any) => void) => {
    return (e: any) => {
      const { value, name } = e.target;
      let parsedValue: any = value;

      // Apply pattern restriction for specific fields
      const pattern = getPatternForField(field);
      if (pattern && value !== '') {
        const regex = new RegExp(pattern);
        if (!regex.test(value)) {
          // Don't update if pattern doesn't match
          return;
        }
      }

      // Manually trigger Formik's change handler with the parsed value
      handleChange({
        target: {
          name,
          value: parsedValue,
        },
      });
    };
  };

  const renderFieldInput = ({
    field,
    values,
    handleChange,
    InputComponent,
    design,
    submissionCount,
    errors,
  }: {
    field: FormPublicFieldFragment;
    values: any;
    handleChange: (e: any) => void;
    InputComponent:
      | (({
          field,
          design,
          urlValue,
          onChange,
          value,
          focus,
          error,
          helperText,
        }: any) => React.JSX.Element)
      | undefined;
    design: FormPublicDesignFragment;
    submissionCount?: number | undefined;
    errors: any;
  }) => {
    const customHandleChange = createCustomHandleChange(handleChange);

    if (InputComponent) {
      return (
        <FieldInputWrapper className='test-field-input-wrapper'>
          {InputComponent && (
            <InputComponent
              design={design}
              field={field}
              onChange={customHandleChange}
              value={values[field.id]}
              error={errors[field.id]}
              helperText={errors[field.id]}
              submissionCount={submissionCount}
              {...(field.type === 'checkbox' || field.id === 'NjGXmV'
                ? { isTwoColumn: true }
                : {})}
            />
          )}
        </FieldInputWrapper>
      );
    }
    return null;
  };

  return (
    <Formik
      initialValues={{
        [field.id]: JSON.parse(field.defaultValue),
        submissionCount: submissionCount,
      }}
      onSubmit={handleFormSubmit}
      validate={validateFieldValues}
      enableReinitialize={true}
    >
      {({ values, handleChange, errors }) => (
        <Form className='form-slide' style={{ justifyContent: 'flex-start' }}>
          <Typography
            sx={{ typography: { xs: 'subtitle2' } }}
            style={{
              color: design.colors.question,
              fontWeight: 800,
              fontStyle: 'italic',
              letterSpacing: '4.56px',
            }}
          >
            {field.description.toUpperCase()}
          </Typography>

          <Typography
            sx={{ typography: { xs: 'h5' } }}
            style={{
              color: design.colors.question,
            }}
          >
            {parse(field.title)}{' '}
          </Typography>

          <Typography
            sx={{ fontSize: { xs: '0.6rem' } }}
            style={{
              color: design.colors.question,
              paddingBottom: '40px',
            }}
          >
            {QUESTION} {slideIndex + 1} of {totalQuestions} <br />
          </Typography>

          {field.type === 'radio' && (
            <Typography
              sx={{ typography: { xs: 'caption' } }}
              style={{
                color: design.colors.question,
                fontWeight: 700,
                marginBottom: '1rem',
              }}
            >
              {GUIDANCE_TEXT}
            </Typography>
          )}

          {renderFieldInput({
            field,
            values,
            handleChange,
            InputComponent,
            design,
            submissionCount,
            errors,
          })}

          <StyledBottomNavigation>
            <BottomNavigation
              errors={errors}
              design={design}
              field={field}
              next={next}
              prev={prev}
              slideIndex={slideIndex}
              values={values}
              lastSlideIndex={lastSlideIndex}
              buttonText={BUTTON_TEXT}
              buttonWidth='157px'
            />
          </StyledBottomNavigation>

          <Box
            sx={{
              position: 'absolute',
              zIndex: -1,
              bottom: 0,
              right: 0,
              width: '100%',
              height: '25%',
              pointerEvents: 'none',
            }}
          >
            <Image
              src={orb}
              alt='orb'
              width={800}
              height={407}
              style={{
                objectFit: 'contain',
                width: '800px',
                height: '407px',
                position: 'absolute',
                bottom: 0,
                right: 0,
                zIndex: -1,
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
};
